/**
 * RUM `beforeSend` that drops error events carrying no application signal.
 * Rules and prod numbers behind them: DEV-28546. Must be cheap and must never throw.
 */

export interface RumBeforeSendOptions {
  /** Origin of the app bundle, e.g. `https://app.ninety.io`. A frame from here keeps an error. */
  appOrigin: string;
  /** Opaque `Script error.` events kept per view before the rest are dropped. Default 5. */
  maxScriptErrorsPerView?: number;
}

/** Marketing pixels blocked by the enforced CSP. Their failure is visible in marketing's tools, not ours. */
export const MARKETING_CSP_HOSTS = ['bing.net', 'bing.com', 'g2.com', 'facebook.com', 'hsadspixel.net'];

const CSP_MESSAGE_PREFIX = 'csp_violation:';
const EXTENSION_SCHEMES = ['chrome-extension://', 'moz-extension://', 'safari-web-extension://'];
const SCRIPT_ERROR = 'Script error.';

interface ErrorLike {
  message?: unknown;
  stack?: unknown;
}

interface EventLike {
  type?: unknown;
  view?: { id?: unknown };
  error?: ErrorLike;
}

export function createRumBeforeSend(options: RumBeforeSendOptions): (event: unknown) => boolean {
  const maxScriptErrors = options.maxScriptErrorsPerView ?? 5;
  const appOrigin = options.appOrigin;
  let currentViewId: unknown = undefined;
  let scriptErrorsInView = 0;

  return (event: unknown): boolean => {
    try {
      const e = event as EventLike | null;
      if (!e || e.type !== 'error' || !e.error) return true;
      const message = typeof e.error.message === 'string' ? e.error.message : '';
      const stack = typeof e.error.stack === 'string' ? e.error.stack : '';

      if (message.startsWith(CSP_MESSAGE_PREFIX)) {
        return !isMarketingHost(blockedHost(message));
      }

      if (isExtensionOnlyStack(stack, appOrigin)) return false;

      if (message.includes(SCRIPT_ERROR)) {
        const viewId = e.view?.id;
        if (viewId !== currentViewId) {
          currentViewId = viewId;
          scriptErrorsInView = 0;
        }
        scriptErrorsInView += 1;
        return scriptErrorsInView <= maxScriptErrors;
      }

      return true;
    } catch {
      return true;
    }
  };
}

/** The blocked URL is the first single-quoted token of the message; only its host counts. */
function blockedHost(message: string): string {
  const quoted = /^csp_violation:\s*'([^']+)'/.exec(message);
  if (!quoted) return '';
  try {
    return new URL(quoted[1]).hostname;
  } catch {
    return '';
  }
}

function isMarketingHost(host: string): boolean {
  return host !== '' && MARKETING_CSP_HOSTS.some(h => host === h || host.endsWith('.' + h));
}

/** True when every frame in the stack is extension-origin and none is from the app. */
function isExtensionOnlyStack(stack: string, appOrigin: string): boolean {
  if (!stack) return false;
  const frames = stack.split('\n').filter(line => /^\s*at\s/.test(line));
  if (frames.length === 0) return false;
  if (frames.some(f => f.includes(appOrigin))) return false;
  return frames.every(f => EXTENSION_SCHEMES.some(scheme => f.includes(scheme)));
}
